<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-form :inline="true" :model="query" class="search-form">
      <el-form-item label="平台">
        <el-select v-model="platformFilter" placeholder="请选择平台" clearable @change="fetchData">
          <el-option v-for="item in platformList" :key="item.id" :label="item.platformName" :value="item.platformCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺">
        <el-select v-model="shopFilter" placeholder="请选择店铺" clearable filterable @change="fetchData">
          <el-option v-for="item in shopList" :key="item.id" :label="item.shopName" :value="item.shopCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="授权状态">
        <el-select v-model="statusFilter" placeholder="请选择状态" clearable @change="fetchData">
          <el-option label="已授权" value="Active" />
          <el-option label="待授权" value="Pending" />
          <el-option label="已过期" value="Expired" />
          <el-option label="已撤销" value="Revoked" />
          <el-option label="授权失败" value="Failed" />
        </el-select>
      </el-form-item>
      <el-form-item label="有效性">
        <el-select v-model="validFilter" placeholder="请选择" clearable @change="fetchData">
          <el-option label="有效" :value="true" />
          <el-option label="无效" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="Search" @click="fetchData">查询</el-button>
        <el-button icon="Refresh" @click="resetSearch">重置</el-button>
        <el-button type="warning" icon="Warning" @click="fetchExpiring">即将过期</el-button>
        <el-button type="success" icon="Plus" @click="handleAdd">新增授权</el-button>
      </el-form-item>
    </el-form>

    <!-- 表格区域 -->
    <basic-table :query="query" @handler="fetchData">
      <template #expiresAt="{ row }">
        <span :class="getExpiresClass(row.expiresAt)">
          {{ formatExpiresTime(row.expiresAt) }}
        </span>
      </template>
      <template #isValid="{ row }">
        <el-tag :type="row.isValid ? 'success' : 'danger'">{{ row.isValid ? '有效' : '无效' }}</el-tag>
      </template>
      <template #status="{ row }">
        <el-tag :type="getStatusType(row.status)">{{ getStatusLabel(row.status) }}</el-tag>
      </template>
      <template #toolbar="{ row }">
        <!-- Pending-待授权、Failed-授权失败、Expired-已过期、Revoked-已撤销 显示"去授权"按钮 -->
        <el-button v-if="['Pending', 'Failed', 'Expired', 'Revoked'].includes(row.status)" 
          link type="primary" size="small" icon="Position" @click="handleReauthorize(row)">去授权</el-button>
        <!-- Active-已授权 显示"刷新"按钮 -->
        <el-button v-if="row.status === 'Active'" 
          link type="success" size="small" icon="Refresh" @click="handleRefresh(row)">刷新</el-button>
        <!-- Active-已授权 显示"失效"按钮 -->
        <el-button v-if="row.status === 'Active'" 
          link type="warning" size="small" icon="CircleClose" @click="handleMarkInvalid(row)">失效</el-button>
        <!-- 所有状态都可以删除 -->
        <el-button link type="danger" size="small" icon="Delete" @click="handleDelete(row)">删除</el-button>
      </template>
    </basic-table>

    <!-- 新增授权对话框 -->
    <el-dialog v-model="dialogVisible" title="新增授权" width="600px" @close="handleDialogClose">
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px">
        <el-form-item label="平台配置" prop="platformConfigID">
          <el-select v-model="formData.platformConfigID" placeholder="请选择平台" style="width: 100%" @change="handlePlatformSelect">
            <el-option v-for="item in platformList" :key="item.id" :label="item.platformName" :value="item.id">
              <span>{{ item.platformName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ item.platformCode }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="店铺" prop="platformShopID">
          <el-select v-model="formData.platformShopID" placeholder="请选择店铺" filterable style="width: 100%" @change="handleShopSelect">
            <el-option v-for="item in filteredShopList" :key="item.id" :label="item.shopName" :value="item.id">
              <span>{{ item.shopName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ item.shopCode }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="授权类型" prop="authType">
          <el-select v-model="formData.authType" placeholder="请选择" style="width: 100%">
            <el-option label="OAuth2" value="OAuth2" />
            <el-option label="APIKey" value="APIKey" />
            <el-option label="Token" value="Token" />
          </el-select>
        </el-form-item>
        <el-form-item label="授权模式" prop="authMode">
          <el-select v-model="formData.authMode" placeholder="请选择" style="width: 100%">
            <el-option label="Platform" value="Platform" />
            <el-option label="Shop" value="Shop" />
            <el-option label="ISV" value="ISV" />
          </el-select>
        </el-form-item>
        <el-alert title="授权说明" type="info" :closable="false" style="margin-bottom: 20px">
          点击"开始授权"后，系统将保存授权配置并跳转到平台授权页面。授权成功后，平台会回调我们的系统，自动更新授权令牌和有效期。
        </el-alert>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" icon="Position" @click="handleStartAuthorize" :loading="authorizing">开始授权</el-button>
      </template>
    </el-dialog>

    <!-- 刷新Token对话框 -->
    <el-dialog v-model="refreshDialogVisible" title="刷新访问令牌" width="600px">
      <el-form ref="refreshFormRef" :model="refreshFormData" :rules="refreshFormRules" label-width="120px">
        <el-form-item label="访问令牌" prop="AccessToken">
          <el-input v-model="refreshFormData.accessToken" type="textarea" :rows="3" placeholder="新的访问Token" />
        </el-form-item>
        <el-form-item label="刷新令牌" prop="RefreshToken">
          <el-input v-model="refreshFormData.refreshToken" type="textarea" :rows="2" placeholder="新的刷新Token" />
        </el-form-item>
        <el-form-item label="有效期(秒)" prop="ExpiresIn">
          <el-input-number v-model="refreshFormData.expiresIn" :min="0" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refreshDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleRefreshSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import BasicTable from '@/components/basic-table.vue'
import { getEnabledPlatformList, getEnabledShopList, getExpiringAuths, refreshToken, markAuthInvalid, startAuthorize, reAuthorize } from '@/api/platform'

export default {
  name: 'PlatformAuth',
  components: {
    BasicTable
  },
  data() {
    return {
      query: {
        keyword: '',
        filters: [],
        sidx: 'CreateDate',
        sord: 'descending',
        page: 1,
        rows: 20,
        data: [],
        columns: [
          { type: 'selection', width: '55' },
          { type: 'index', label: '序号', width: '60' },
          { prop: 'platformConfigID', type: 'hidden', label: '平台配置ID', width: '0', hide: true },
          { prop: 'platformShopID', type: 'hidden', label: '店铺ID', width: '0', hide: true },
          { prop: 'platformCode', label: '平台编码', width: '130', sortable: true },
          { prop: 'shopCode', label: '店铺编码', width: '150', sortable: true },
          { prop: 'authType', label: '授权类型', width: '110' },
          { prop: 'authMode', label: '授权模式', width: '110' },
          { type: 'template', prop: 'expiresAt', label: '过期时间', width: '180', sortable: true },
          { prop: 'authorizeDate', label: '授权时间', width: '160', sortable: true },
          { prop: 'refreshDate', label: '刷新时间', width: '160', sortable: true },
          { type: 'template', prop: 'isValid', label: '是否有效', width: '90' },
          { type: 'template', prop: 'status', label: '状态', width: '100' },
          { prop: 'createDate', label: '创建时间', width: '160', sortable: true },
          { type: 'toolbar', label: '操作', width: '280', fixed: 'right' }
        ]
      },
      platformFilter: '',
      shopFilter: '',
      statusFilter: '',
      validFilter: '',
      platformList: [],
      shopList: [],
      dialogVisible: false,
      refreshDialogVisible: false,
      authorizing: false,
      currentAuthId: '',
      formData: {},
      refreshFormData: {},
      showAccessToken: false,
      showRefreshToken: false,
      formRules: {
        platformConfigID: [{ required: true, message: '请选择平台配置', trigger: 'change' }],
        platformShopID: [{ required: true, message: '请选择店铺', trigger: 'change' }],
        authType: [{ required: true, message: '请选择授权类型', trigger: 'change' }],
        authMode: [{ required: true, message: '请选择授权模式', trigger: 'change' }]
      },
      refreshFormRules: {
        AccessToken: [{ required: true, message: '请输入访问令牌', trigger: 'blur' }],
        RefreshToken: [{ required: true, message: '请输入刷新令牌', trigger: 'blur' }],
        ExpiresIn: [{ required: true, message: '请输入有效期', trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredShopList() {
      if (!this.formData.platformConfigID) return this.shopList
      const platform = this.platformList.find(p => p.id === this.formData.platformConfigID)
      if (!platform) return this.shopList
      return this.shopList.filter(s => s.platformCode === platform.platformCode)
    }
  },
  mounted() {
    this.$kaung.init(this.$options.name)
    this.loadPlatformList()
    this.loadShopList()
    this.fetchData()
  },
  methods: {
    builder() {
      let { data, records, total, ...params } = { ...this.query }
      
      // 重置并添加筛选条件
      params.filters = []
      if (this.platformFilter) {
        params.filters.push({ name: 'platformCode', value: this.platformFilter, operate: 0 })
      }
      if (this.shopFilter) {
        params.filters.push({ name: 'shopCode', value: this.shopFilter, operate: 0 })
      }
      if (this.statusFilter) {
        params.filters.push({ name: 'status', value: this.statusFilter, operate: 0 })
      }
      if (this.validFilter !== '') {
        params.filters.push({ name: 'isValid', value: this.validFilter, operate: 0 })
      }
      return params
    },
    fetchData() {
      this.$kaung.search(this.builder()).then(res => {
        this.query = { ...res.data }
      })
    },
    resetSearch() {
      this.platformFilter = ''
      this.shopFilter = ''
      this.statusFilter = ''
      this.validFilter = ''
      this.fetchData()
    },
    fetchExpiring() {
      getExpiringAuths(60).then(res => {
        this.query.data = res.data || []
        this.$message.info(`共找到 ${this.query.data.length} 条即将过期的授权`)
      })
    },
    loadPlatformList() {
      getEnabledPlatformList().then(res => {
        this.platformList = res.data || []
      })
    },
    loadShopList() {
      getEnabledShopList().then(res => {
        this.shopList = res.data || []
      })
    },
    handlePlatformSelect(platformConfigID) {
      const platform = this.platformList.find(p => p.id === platformConfigID)
      if (platform) {
        this.formData.platformCode = platform.platformCode
      }
    },
    handleShopSelect(shopId) {
      const shop = this.shopList.find(s => s.id === shopId)
      if (shop) {
        this.formData.shopCode = shop.shopCode
      }
    },
    handleAdd() {
      this.formData = {
        platformConfigID: '',
        platformShopID: '',
        platformCode: '',
        shopCode: '',
        authType: 'OAuth2',
        authMode: 'Platform'
      }
      this.dialogVisible = true
    },
    handleReauthorize(row) {
      // 重新授权：基于现有记录的平台和店铺信息发起授权
      this.$confirm('确定要重新授权吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.authorizing = true
        const authData = {
          PlatformConfigID: row.platformConfigID,
          PlatformShopID: row.platformShopID,
          AuthType: row.authType,
          AuthMode: row.authMode
        }
        
        reAuthorize(row.id, authData).then(res => {
          if (res.data && res.data.authUrl) {
            const { platformName, shopName, message } = res.data
            this.$message.success(message || `${platformName} - ${shopName} 授权记录已创建`)
            // 打开新窗口进行授权
            window.open(res.data.authUrl, '_blank', 'width=800,height=600')
            // 刷新列表
            setTimeout(() => {
              this.fetchData()
            }, 1000)
          } else {
            this.$message.error('获取授权地址失败')
          }
        }).catch(() => {
          this.$message.error('启动授权失败')
        }).finally(() => {
          this.authorizing = false
        })
      })
    },
    handleStartAuthorize() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        
        this.authorizing = true
        const authData = {
          PlatformConfigID: this.formData.platformConfigID,
          PlatformShopID: this.formData.platformShopID,
          AuthType: this.formData.authType,
          AuthMode: this.formData.authMode
        }
        
        startAuthorize(authData).then(res => {
          if (res.data && res.data.authUrl) {
            const { platformName, shopName, message } = res.data
            this.$message.success(message || `${platformName} - ${shopName} 正在跳转到授权页面...`)
            this.dialogVisible = false
            // 打开新窗口进行授权
            window.open(res.data.authUrl, '_blank', 'width=800,height=600')
            // 刷新列表
            setTimeout(() => {
              this.fetchData()
            }, 1000)
          } else {
            this.$message.error('获取授权地址失败')
          }
        }).catch(() => {
          this.$message.error('启动授权失败')
        }).finally(() => {
          this.authorizing = false
        })
      })
    },
    handleRefresh(row) {
      this.currentAuthId = row.id
      this.refreshFormData = {
        accessToken: '',
        refreshToken: '',
        expiresIn: 86400
      }
      this.refreshDialogVisible = true
    },
    handleRefreshSubmit() {
      this.$refs.refreshFormRef.validate(valid => {
        if (!valid) return
        
        refreshToken(this.currentAuthId, this.refreshFormData).then(() => {
          this.$message.success('刷新成功')
          this.refreshDialogVisible = false
          this.fetchData()
        })
      })
    },
    handleMarkInvalid(row) {
      this.$confirm('确定要标记该授权为无效吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        markAuthInvalid(row.id).then(() => {
          this.$message.success('标记成功')
          this.fetchData()
        })
      })
    },
    handleDelete(row) {
      this.$confirm('确定要删除该授权吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$kaung.delete(row.id).then(() => {
          this.$message.success('删除成功')
          this.fetchData()
        })
      })
    },
    handleDialogClose() {
      this.$refs.formRef?.resetFields()
    },
    formatExpiresTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return this.$util.fmtDate(date, 'yyyy-MM-dd HH:mm:ss')
    },
    getExpiresClass(timestamp) {
      if (!timestamp) return ''
      const now = Date.now() / 1000
      const diff = timestamp - now
      if (diff < 0) return 'expired-time'
      if (diff < 3600) return 'warning-time'
      return ''
    },
    getStatusType(status) {
      const statusMap = {
        'Active': 'success',
        'Pending': 'info',
        'Expired': 'warning',
        'Revoked': 'danger',
        'Failed': 'danger'
      }
      return statusMap[status] || ''
    },
    getStatusLabel(status) {
      const labelMap = {
        'Active': '已授权',
        'Pending': '待授权',
        'Expired': '已过期',
        'Revoked': '已撤销',
        'Failed': '授权失败'
      }
      return labelMap[status] || status
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}
.search-form {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 4px;
}
.expired-time {
  color: #f56c6c;
  font-weight: bold;
}
.warning-time {
  color: #e6a23c;
  font-weight: bold;
}
</style>
